import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../styles/HomePage.css';

// Import the images directly
import cloudIcon from '../assets/cloud.png';
import analyticsIcon from '../assets/combo-chart.png';
import heroImage from '../assets/hero.jpg';
import supplyChainIcon from '../assets/in-transit.png';
import erpIcon from '../assets/process.png';


const HomePage = () => {
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);
  const navigate = useNavigate();
  
  // Trigger animations when component mounts
  useEffect(() => {
    setIsVisible(true);
    
    // Add visible class to hero elements after a longer delay
    const timer = setTimeout(() => {
      const heroElements = document.querySelectorAll('.hero-animated');
      heroElements.forEach(el => {
        el.classList.add('visible');
      });
    }, 200); // Slightly longer delay before adding visible class
    
    return () => clearTimeout(timer);
  }, []);

  // Handle navigation to feature detail pages
  const handleFeatureLearnMore = (featureSlug) => {
    navigate(`/features/${featureSlug}`);
  };

  // Feature cards data
  const features = [
   
    {
      title: 'Cloud Solutions',
      description: 'Access your business applications anywhere, anytime with our secure cloud infrastructure.',
      icon: cloudIcon, // Using imported image
      slug: 'cloud-solutions'
    },
    {
      title: 'Business Analytics',
      description: 'Make data-driven decisions with comprehensive reporting and predictive analytics.',
      icon: analyticsIcon, // Using imported image
      slug: 'business-analytics'
    },
    {
      title: 'Supply Chain Management',
      description: 'Optimize your entire supply chain from procurement to delivery with real-time insights.',
      icon: supplyChainIcon, // Using imported image
      slug: 'supply-chain-management'
    },
    {
      title: 'Intelligent ERP',
      description: 'Drive digital transformation with our AI-powered ERP solutions that adapt to your business needs.',
      icon: erpIcon, // Using imported image
      slug: 'intelligent-erp'
    }
  ];

  // Industry solutions data
  const solutions = [
    {
      industry: 'Manufacturing',
      description: 'Streamline operations, optimize production, and reduce costs with our manufacturing solutions.',
      icon: '🏭'
    },
    {
      industry: 'Retail',
      description: 'Create seamless customer experiences across all channels with our retail management system.',
      icon: '🛒'
    },
    {
      industry: 'Healthcare',
      description: 'Enhance patient care and optimize administrative processes with our healthcare solutions.',
      icon: '🏥'
    },
    {
      industry: 'Financial Services',
      description: 'Meet regulatory requirements and deliver innovative financial services with our solutions.',
      icon: '🏦'
    }
  ];

  // Mock testimonial data
  const testimonials = [
    {
      quote: "VCRP has transformed our business processes. We've seen a 30% increase in efficiency across departments.",
      author: "Manori",
      title: "CTM - Peliyagoda",
      avatar: "M"
    },
    {
      quote: "The implementation was smooth and the support team has been exceptional. Our ROI was achieved within the first year.",
      author: "Sajan",
      title: "CTM - Peliyagoda",
      avatar: "S"
    }
  ];

  // Statistics data
  const stats = [
    { number: "7+", label: "Businesses Using Our Solution" },
    { number: "3+", label: "Countries" },
    { number: "99.9%", label: "Uptime" },
    { number: "24/7", label: "Support" }
  ];

  return (
    <>
      {/* Hero Section */}
      <section 
        className="hero-section" 
        ref={heroRef}
        style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center',
          paddingTop: '2rem',
          paddingBottom: '2rem',
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Dark overlay with transition */}
        <div 
          className="hero-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 1,
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 2s ease-in' // Changed to 2s
          }}
        ></div>
        
        <Container style={{ position: 'relative', zIndex: 2 }}>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h1 className="text-white hero-animated">Transform Your Business with Intelligent VCRP Solution</h1>
              <p className="lead mb-4 text-white hero-animated">
                Streamline operations, gain real-time insights, and accelerate growth with our comprehensive 
                <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}> V</span>alue 
                <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>C</span>raft 
                <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}> R</span>esource 
                <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}> P</span>lanning system.
              </p>
              <div className="hero-animated">
                <Button variant="light" size="lg" className="me-3">Watch Demo</Button>
                <Button variant="outline-light" size="lg">Contact Sales</Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold">Why our VCRP solution</h2>
            <p className="lead text-muted">Discover how our comprehensive tools can transform your business</p>
          </div>
          <Row xs={1} md={2} lg={4} className="g-4">
            {features.map((feature, idx) => (
              <Col key={idx}>
                <Card className="feature-card text-center h-100">
                  <Card.Body>
                    <div className="feature-icon d-flex justify-content-center align-items-center mb-4" 
                         style={{ 
                           minHeight: '80px', 
                           display: 'flex', 
                           justifyContent: 'center', 
                           alignItems: 'center' 
                         }}>
                      <img 
                        src={feature.icon} 
                        alt={feature.title} 
                        width="64" 
                        height="64" 
                        style={{ maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </div>
                    <Card.Title className="fw-bold mb-3">{feature.title}</Card.Title>
                    <Card.Text>{feature.description}</Card.Text>
                  </Card.Body>
                  <Card.Footer className="bg-white border-0">
                    <Button 
                      variant="link" 
                      className="text-primary p-0"
                      onClick={() => handleFeatureLearnMore(feature.slug)}
                    >
                      Learn more →
                    </Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Solutions By Industry Section */}
      {/* <section className="solutions-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold">Industry-Specific Solutions</h2>
            <p className="lead text-muted">Tailored to meet the unique challenges of your industry</p>
          </div>
          <Row xs={1} md={2} className="g-4">
            {solutions.map((solution, idx) => (
              <Col key={idx}>
                <div className="solution-item">
                  <div className="d-flex align-items-center mb-3">
                    <div className="fs-1 me-3">{solution.icon}</div>
                    <h3 className="mb-0 fw-bold">{solution.industry}</h3>
                  </div>
                  <p>{solution.description}</p>
                  <Button variant="outline-primary">Discover Solutions</Button>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section> */}

      {/* Testimonials Section */}
      {/* <section className="testimonials-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold">Trusted by Industry Leaders</h2>
            <p className="lead text-muted">See what our customers have to say</p>
          </div>
          <Row xs={1} md={2} className="g-4">
            {testimonials.map((testimonial, idx) => (
              <Col key={idx}>
                <Card className="testimonial-card h-100 border-0">
                  <Card.Body>
                    <blockquote className="blockquote mb-0">
                      <p>"{testimonial.quote}"</p>
                      <footer className="blockquote-footer mt-3">
                        <div className="d-flex align-items-center">
                          <div 
                            className="avatar-circle me-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: '50px',
                              height: '50px',
                              borderRadius: '50%',
                              backgroundColor: '#007bff',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '16px'
                            }}
                          >
                            {testimonial.avatar}
                          </div>
                          <div>
                            <strong>{testimonial.author}</strong><br/>
                            <small className="text-muted">{testimonial.title}</small>
                          </div>
                        </div>
                      </footer>
                    </blockquote>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section> */}

      {/* Statistics Section */}
      <section className="stats-section">
        <Container>
          <Row xs={2} md={4}>
            {stats.map((stat, idx) => (
              <Col key={idx} className="mb-4 mb-md-0">
                <div className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div>{stat.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Product Highlights Section */}
      {/* <section className="py-5">
        <Container>
          <Row className="align-items-center py-5">
            <Col lg={6} className="mb-4 mb-lg-0">
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80" 
                alt="Financial Dashboard" 
                className="img-fluid rounded shadow"
              />
            </Col>
            <Col lg={6}>
              <h2 className="fw-bold mb-4">Financial Management Made Simple</h2>
              <p className="lead">Gain complete visibility into your financial operations with our comprehensive tools.</p>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Real-time financial reporting and analytics
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Automated accounts payable and receivable
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Simplified tax compliance and management
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Cash flow forecasting and planning
                </li>
              </ul>
              <Button variant="primary" className="mt-3">Explore Financial Tools</Button>
            </Col>
          </Row>

          <Row className="align-items-center py-5 flex-lg-row-reverse">
            <Col lg={6} className="mb-4 mb-lg-0">
              <img 
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80" 
                alt="HR Management" 
                className="img-fluid rounded shadow"
              />
            </Col>
            <Col lg={6}>
              <h2 className="fw-bold mb-4">Streamlined HR & Workforce Management</h2>
              <p className="lead">Empower your HR team with tools to manage the entire employee lifecycle efficiently.</p>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Centralized employee data management
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Automated payroll processing and compliance
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Performance management and feedback tools
                </li>
                <li className="mb-3">
                  <i className="fas fa-check-circle text-primary me-2"></i>
                  Recruitment and onboarding automation
                </li>
              </ul>
              <Button variant="primary" className="mt-3">Explore HR Solutions</Button>
            </Col>
          </Row>
        </Container>
      </section> */}

      {/* Call-to-Action Section */}
      <section className="cta-section">
        <Container>
          <div className="py-5">
            <h2 className="fw-bold mb-3">Ready to transform your business?</h2>
            <p className="lead text-muted mb-4">Join thousands of companies that trust our ERP solutions</p>
            <Button variant="primary" size="lg" className="me-3" onClick={() => navigate('/contact')}>Contact Sales</Button>
          </div>
        </Container>
      </section>
    </>
  );
};

export default HomePage;
